import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridInitialState, GridRowId, GridSortModel, useGridApiRef } from '@mui/x-data-grid';
import { ptBR } from '@mui/x-data-grid/locales';
import { TableTemplate } from 'types/tableTemplate';
import { ExcelCell } from 'helpers/exportToExcel';
import DataGridToolbar from './DataGridToolbar';

const ACTION_FIELDS = ['action', 'deleteAction'];

export type DataGridServerSort = {
  getDirection(originalId: string): 'asc' | 'desc' | null;
  onSort(originalId: string): void;
};

type DataGridListProps<T> = {
  template: TableTemplate[];
  rows: T[];
  getRowId(row: T): GridRowId;
  // Conteúdo customizado da célula (ações, status, nome + procedimento...). Sem isso, mostra row[id].
  renderCells?: Record<string, (row: T) => ReactNode>;
  // Valor que vai para o Excel quando difere do texto da célula.
  exportValues?: Record<string, (row: T) => string | number | null>;
  onRowClick?(row: T): void;
  getRowClassName?(row: T): string;
  serverSort?: DataGridServerSort;
  fileName: string;
  sheetName?: string;
  rowHeight?: number;
};

function toText(value: unknown) {
  return value === null || value === undefined ? '' : String(value);
}

type SavedLayout = {
  orderedFields?: string[];
  visibility?: Record<string, boolean>;
  widths?: Record<string, number>;
};

function storageKey(name: string) {
  return `datagrid-layout:${name}`;
}

function readLayout(name: string): SavedLayout {
  try {
    return JSON.parse(localStorage.getItem(storageKey(name)) || '{}');
  } catch {
    return {};
  }
}

function normalize(text: string) {
  return text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

function DataGridList<T>({
  template,
  rows,
  getRowId,
  renderCells = {},
  exportValues = {},
  onRowClick,
  getRowClassName,
  serverSort,
  fileName,
  sheetName,
  rowHeight = 44,
}: DataGridListProps<T>) {
  const apiRef = useGridApiRef();

  // A reordenação de colunas por arrastar é recurso pago do DataGrid; a ordem é mantida aqui e salva por tabela.
  const [order, setOrder] = useState<string[]>(() => {
    const fields = template.map(item => item.id);
    const known = (readLayout(fileName).orderedFields ?? []).filter(field => fields.includes(field));
    return [...known, ...fields.filter(field => !known.includes(field))];
  });

  // Move a coluna uma posição para a esquerda (-1) ou para a direita (+1) na ordem da tabela.
  function moveColumn(field: string, step: -1 | 1) {
    setOrder(current => {
      const target = current[current.indexOf(field) + step];
      if (!target || ACTION_FIELDS.includes(target)) return current;

      const next = [...current];
      next[current.indexOf(field)] = target;
      next[current.indexOf(target)] = field;
      return next;
    });
  }

  const columnItems = order
    .filter(field => !ACTION_FIELDS.includes(field))
    .map(field => ({ field, label: template.find(item => item.id === field)?.description ?? field }));

  const columns = useMemo<GridColDef[]>(
    () =>
      [...template]
        .sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id))
        .map(item => {
          const isAction = ACTION_FIELDS.includes(item.id);
          const isNumber = item.dataType === 'number';
          const render = renderCells[item.id];

          return {
            field: item.id,
            headerName: item.description,
            width: item.width,
            minWidth: 0,
            align: isNumber ? 'right' : isAction ? 'center' : 'left',
            headerAlign: isNumber ? 'right' : isAction ? 'center' : 'left',
            sortable: !isAction,
            filterable: !isAction,
            hideable: !isAction,
            disableColumnMenu: isAction,
            disableExport: isAction,
            cellClassName: `cell-${item.id}`,
            // A busca rápida compara com o texto que o usuário vê na célula, não com o valor bruto.
            getApplyQuickFilterFn: (search: string) => {
              const term = normalize(search);
              return (_value: unknown, row: any) => normalize(getSearchText(item.id, row)).includes(term);
            },
            // A ordenação usa o valor bruto (value_total, appointment_date...) e não o texto formatado.
            valueGetter: (_value, row) => row[item.originalId] ?? row[item.id],
            renderCell: ({ row }) =>
              render ? (
                render(row as T)
              ) : (
                <Typography variant="body2" noWrap title={toText(row[item.id])}>
                  {toText(row[item.id])}
                </Typography>
              ),
          };
        }),
    [template, renderCells, exportValues, order],
  );

  function getSearchText(field: string, row: T) {
    const custom = exportValues[field];
    return toText(custom ? custom(row) : (row as any)[field]);
  }

  // Largura, visibilidade e posição das colunas salvas por tabela (fileName); campos que não existem mais são ignorados.
  const [initialState] = useState<GridInitialState>(() => {
    const saved = readLayout(fileName);
    const fields = template.map(item => item.id);

    const columnVisibilityModel: Record<string, boolean> = {
      ...Object.fromEntries(template.filter(item => item.notShow).map(item => [item.id, false])),
      ...saved.visibility,
    };

    const dimensions = Object.fromEntries(
      Object.entries(saved.widths ?? {})
        .filter(([field]) => fields.includes(field))
        .map(([field, width]) => [field, { width }]),
    );

    return { columns: { columnVisibilityModel, dimensions } };
  });

  const firstRender = useRef(true);

  // Salva só quando o usuário muda a ordem, não na montagem (senão congelaria as larguras do template).
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    saveLayout();
    // eslint-disable-next-line
  }, [order]);

  function saveLayout() {
    const state = apiRef.current?.exportState().columns;
    if (!state) return;

    const layout: SavedLayout = {
      orderedFields: order,
      visibility: state.columnVisibilityModel,
      widths: Object.fromEntries(
        Object.entries(state.dimensions ?? {})
          .filter(([, dimension]) => typeof dimension?.width === 'number')
          .map(([field, dimension]) => [field, dimension.width as number]),
      ),
    };

    try {
      localStorage.setItem(storageKey(fileName), JSON.stringify(layout));
    } catch {
      // sem localStorage (modo privado, cota cheia): a tabela só não lembra o layout
    }
  }

  // Cores definidas por coluna no template (ex.: "Vl. Pago" verde).
  const columnStyles = useMemo(
    () =>
      Object.fromEntries(
        template
          .filter(item => item.backgroundColor || item.color)
          .map(item => [`& .cell-${item.id}`, { backgroundColor: item.backgroundColor, color: item.color }]),
      ),
    [template],
  );

  const sortModel = useMemo<GridSortModel | undefined>(() => {
    if (!serverSort) return undefined;
    for (const item of template) {
      const sort = serverSort.getDirection(item.originalId);
      if (sort) return [{ field: item.id, sort }];
    }
    return [];
  }, [serverSort, template]);

  function getExportCell(field: string, row: T): ExcelCell {
    const item = template.find(column => column.id === field);
    const custom = exportValues[field];
    if (custom) return { value: custom(row) };

    const display = toText((row as any)[field]);
    const raw = item ? (row as any)[item.originalId] : undefined;

    // Valores em R$ vão como número, para o Excel conseguir somar.
    if (item?.dataType === 'number' && typeof raw === 'number' && display.startsWith('R$')) {
      return { value: raw, money: true };
    }

    return { value: display };
  }

  function handleSortModelChange(model: GridSortModel) {
    const field = model[0]?.field;
    const item = template.find(column => column.id === field);
    if (serverSort && item) serverSort.onSort(item.originalId);
  }

  return (
    <Box sx={{ height: 'calc(100vh - 330px)', minHeight: 420, backgroundColor: '#fff' }}>
      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={getRowId}
        rowHeight={rowHeight}
        columnHeaderHeight={42}
        hideFooter
        disableRowSelectionOnClick
        showToolbar
        disableColumnMenu
        disableColumnFilter
        localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
        sortingOrder={['asc', 'desc']}
        sortingMode={serverSort ? 'server' : 'client'}
        sortModel={sortModel}
        onSortModelChange={serverSort ? handleSortModelChange : undefined}
        apiRef={apiRef}
        initialState={initialState}
        onColumnWidthChange={saveLayout}
        onColumnVisibilityModelChange={saveLayout}
        onRowClick={onRowClick ? ({ row }) => onRowClick(row as T) : undefined}
        getRowClassName={getRowClassName ? ({ row }) => getRowClassName(row as T) : undefined}
        slots={{ toolbar: DataGridToolbar }}
        slotProps={{
          toolbar: {
            columnItems,
            onMoveColumn: moveColumn,
            fileName,
            sheetName: sheetName ?? fileName,
            getExportCell: getExportCell as any,
          },
        }}
        sx={{
          border: 0,
          '& .MuiDataGrid-columnHeaderTitle': { fontSize: 12, fontWeight: 600, color: 'primary.main' },
          '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center', fontSize: 12 },
          '& .MuiDataGrid-row': onRowClick ? { cursor: 'pointer' } : undefined,
          '& .inactive-row': { opacity: 0.6 },
          ...columnStyles,
        }}
      />
    </Box>
  );
}

export default DataGridList;
